/**
 * Statement generation, modelled on the `LnrAccountStatement` workflow.
 *
 * In production this is two workflow steps — `reqAccountStatement` captures what to produce, and
 * `genAccountStatement` produces it. The prototype has no workflow engine and no reviewer, so the
 * two collapse into one screen: fill the request, press Generate, and the document appears in the
 * generated table with a download link.
 *
 * Request types and period presets mirror the production step so the shape is recognisable —
 * including the Sri Lankan tax year, which runs 1 April – 31 March.
 */

import dayjs from 'dayjs';

import { FUNDS, TRANSACTION_KIND_LABELS, SEGMENT_LABELS } from '../data/seed';

const ISO = 'YYYY-MM-DD';

/** The DRS document types from `reqAccountStatement/constants.js`. */
export const REQUEST_TYPES = {
    ACCOUNT_STATEMENT: 'AccountStatement',
    TAX_REPORT: 'TaxReport',
    THIRD_PARTY_DOCUMENT: 'ThirdPartyDocument',
    UNIT_PRICE_REPORT: 'UnitPriceReport',
    OTHER: 'Other'
};

export const REQUEST_TYPE_OPTIONS = [
    { value: REQUEST_TYPES.ACCOUNT_STATEMENT, label: 'Account Statement' },
    { value: REQUEST_TYPES.TAX_REPORT, label: 'Tax Report' },
    { value: REQUEST_TYPES.THIRD_PARTY_DOCUMENT, label: 'Statement to Third Party' },
    { value: REQUEST_TYPES.UNIT_PRICE_REPORT, label: 'Unit Price Report' },
    { value: REQUEST_TYPES.OTHER, label: 'Other' }
];

/** Only a Tax Report is produced per fund — one document slot per selected fund, as in production. */
export const isPerFundType = (requestType) => requestType === REQUEST_TYPES.TAX_REPORT;

/** A third-party statement is the one type addressed to someone other than the client. */
export const needsRecipient = (requestType) => requestType === REQUEST_TYPES.THIRD_PARTY_DOCUMENT;

/** Unit prices are a fund-level property — no client needed. */
export const isClientScoped = (requestType) => requestType !== REQUEST_TYPES.UNIT_PRICE_REPORT;

/* ── periods ─────────────────────────────────────────────────────────────────────────────────── */

export const PERIOD_TYPES = {
    CUSTOM: 'custom',
    TAX_YEAR: 'taxYear',
    QUARTER: 'quarter',
    MONTH: 'month',
    CALENDAR_YEAR: 'calendarYear'
};

export const PERIOD_TYPE_OPTIONS = [
    { value: PERIOD_TYPES.CUSTOM, label: 'Custom Range' },
    { value: PERIOD_TYPES.TAX_YEAR, label: 'Tax Year (Apr–Mar)' },
    { value: PERIOD_TYPES.QUARTER, label: 'Quarter' },
    { value: PERIOD_TYPES.MONTH, label: 'Month' },
    { value: PERIOD_TYPES.CALENDAR_YEAR, label: 'Calendar Year' }
];

/** Label for the concrete-period select, which changes with the chosen type — as in production. */
export const PERIOD_VALUE_LABELS = {
    [PERIOD_TYPES.TAX_YEAR]: 'Tax Year',
    [PERIOD_TYPES.QUARTER]: 'Quarter',
    [PERIOD_TYPES.MONTH]: 'Month',
    [PERIOD_TYPES.CALENDAR_YEAR]: 'Year'
};

/** The Sri Lankan year of assessment starts in April, so April is month 1 of a tax year. */
const TAX_YEAR_START_MONTH = 4;
const startOfTaxYear = (startYear) => dayjs(`${startYear}-04-01`);

const taxYearStartYearOf = (date) =>
    date.month() + 1 >= TAX_YEAR_START_MONTH ? date.year() : date.year() - 1;

/** Concrete periods to offer for a type, newest first, each carrying its own resolved bounds. */
export const periodOptions = (periodType) => {
    const now = dayjs();
    const currentTaxYear = taxYearStartYearOf(now);

    if (periodType === PERIOD_TYPES.TAX_YEAR) {
        return Array.from({ length: 5 }, (_, i) => {
            const year = currentTaxYear - i;
            const start = startOfTaxYear(year);
            return {
                value: `taxYear:${year}`,
                label: `${year}/${year + 1}`,
                startDate: start.format(ISO),
                endDate: start.add(1, 'year').subtract(1, 'day').format(ISO)
            };
        });
    }

    if (periodType === PERIOD_TYPES.QUARTER) {
        const labels = ['Apr–Jun', 'Jul–Sep', 'Oct–Dec', 'Jan–Mar'];
        return Array.from({ length: 8 }, (_, i) => {
            const start = startOfTaxYear(currentTaxYear).subtract(i * 3, 'month');
            const taxYear = taxYearStartYearOf(start);
            const quarter = Math.floor(start.diff(startOfTaxYear(taxYear), 'month') / 3) + 1;
            return {
                value: `quarter:${taxYear}:${quarter}`,
                label: `${taxYear}/${taxYear + 1} Q${quarter} (${labels[quarter - 1]})`,
                startDate: start.format(ISO),
                endDate: start.add(3, 'month').subtract(1, 'day').format(ISO)
            };
        });
    }

    if (periodType === PERIOD_TYPES.MONTH) {
        return Array.from({ length: 12 }, (_, i) => {
            const start = now.startOf('month').subtract(i, 'month');
            return {
                value: `month:${start.format('YYYY-MM')}`,
                label: start.format('MMMM YYYY'),
                startDate: start.format(ISO),
                endDate: start.endOf('month').format(ISO)
            };
        });
    }

    if (periodType === PERIOD_TYPES.CALENDAR_YEAR) {
        return Array.from({ length: 5 }, (_, i) => {
            const year = now.year() - i;
            return {
                value: `calendarYear:${year}`,
                label: String(year),
                startDate: `${year}-01-01`,
                endDate: `${year}-12-31`
            };
        });
    }

    return [];
};

/* ── document content ────────────────────────────────────────────────────────────────────────── */

const money = (value) => Number(value || 0).toFixed(2);
const units = (value) => Number(value || 0).toFixed(4);

const inPeriod = (date, startDate, endDate) => {
    if (!startDate || !endDate) return true;
    const d = dayjs(date);
    return d.isValid() && !d.isBefore(dayjs(startDate), 'day') && !d.isAfter(dayjs(endDate), 'day');
};

const HOLDING_COLUMNS = [
    { key: 'strategyName', title: 'Strategy' },
    { key: 'activationCode', title: 'Activation code' },
    { key: 'fundId', title: 'Fund' },
    { key: 'units', title: 'Units', align: 'right' },
    { key: 'unitPrice', title: 'Unit price', align: 'right' },
    { key: 'value', title: 'Value (LKR)', align: 'right' }
];

const TRANSACTION_COLUMNS = [
    { key: 'transactionId', title: 'Transaction ID', mono: true },
    { key: 'transactionDate', title: 'Date' },
    { key: 'kind', title: 'Transaction' },
    { key: 'fundId', title: 'Fund' },
    { key: 'strategyName', title: 'Strategy' },
    { key: 'unitPrice', title: 'Unit price', align: 'right' },
    { key: 'units', title: 'Units', align: 'right' },
    { key: 'amount', title: 'Value (LKR)', align: 'right' },
    { key: 'status', title: 'Status' }
];

const holdingRows = (accounts, fundPrices, fundFilter) =>
    accounts.flatMap((account) =>
        account.strategies.flatMap((strategy) =>
            FUNDS.filter(
                (fund) =>
                    Number(strategy.holdings?.[fund.fundId] || 0) > 0 &&
                    (!fundFilter?.length || fundFilter.includes(fund.fundId))
            ).map((fund) => {
                const held = Number(strategy.holdings[fund.fundId]);
                return {
                    key: `${account.accountId}:${strategy.strategyId}:${fund.fundId}`,
                    accountName: account.accountName,
                    strategyName: strategy.strategyName,
                    activationCode: strategy.activationCode,
                    fundId: fund.fundId,
                    units: units(held),
                    unitPrice: money(fundPrices[fund.fundId]),
                    value: money(held * fundPrices[fund.fundId])
                };
            })
        )
    );

const transactionRows = (transactions, accountIds, request) =>
    transactions
        .filter((txn) => (accountIds ? accountIds.has(txn.accountId) : true))
        .filter((txn) => (request.fundIds?.length ? request.fundIds.includes(txn.fundId) : true))
        .filter((txn) => inPeriod(txn.transactionDate, request.startDate, request.endDate))
        .map((txn) => ({
            key: txn.transactionId,
            transactionId: txn.transactionId,
            transactionDate: txn.transactionDate,
            kind: TRANSACTION_KIND_LABELS[txn.kind] || txn.kind,
            fundId: txn.fundId,
            strategyName: txn.strategyName,
            unitPrice: money(txn.unitPrice),
            units: units(txn.units),
            amount: money(txn.amount),
            status: txn.status
        }));

/**
 * Build one generated document from a request.
 *
 * @param {object} request  the captured request (type, client, funds, period, recipient)
 * @param {object} book     { accounts, transactions, fundPrices, navBatches }
 * @returns {{columns: Array, rows: Array}}
 */
export const buildDocument = (request, book) => {
    const { accounts, transactions, fundPrices, navBatches } = book;

    const scoped = request.accountId
        ? accounts.filter((account) => account.accountId === request.accountId)
        : accounts;
    const accountIds = request.accountId ? new Set([request.accountId]) : null;

    switch (request.reportType) {
        case REQUEST_TYPES.TAX_REPORT:
            // Production produces one slot per selected fund; the prototype produces one document
            // per fund too — this is the body of ONE of them, so it is already fund-scoped.
            return {
                columns: [{ key: 'accountName', title: 'Client' }, ...TRANSACTION_COLUMNS],
                rows: transactionRows(transactions, accountIds, request).map((row) => ({
                    ...row,
                    accountName: scoped[0]?.accountName || '—'
                }))
            };

        case REQUEST_TYPES.UNIT_PRICE_REPORT:
            return {
                columns: [
                    { key: 'uploadedDate', title: 'Date' },
                    { key: 'fundId', title: 'Fund' },
                    { key: 'navValue', title: 'NAV value (LKR)', align: 'right' },
                    { key: 'issuedUnits', title: 'Units in issue', align: 'right' },
                    { key: 'unitPrice', title: 'Unit price', align: 'right' },
                    { key: 'status', title: 'Batch status' }
                ],
                rows: navBatches
                    .filter((batch) => inPeriod(batch.uploadedDate, request.startDate, request.endDate))
                    .flatMap((batch) =>
                        batch.entries
                            .filter((entry) =>
                                request.fundIds?.length ? request.fundIds.includes(entry.fundId) : true
                            )
                            .map((entry) => ({
                                key: `${batch.id}:${entry.fundId}`,
                                uploadedDate: batch.uploadedDate,
                                fundId: entry.fundId,
                                navValue: money(entry.navValue),
                                issuedUnits: units(entry.issuedUnits),
                                unitPrice: money(entry.unitPrice),
                                status: batch.status
                            }))
                    )
            };

        case REQUEST_TYPES.THIRD_PARTY_DOCUMENT:
            // A confirmation of holdings addressed to a third party — position, not activity.
            return {
                columns: [{ key: 'accountName', title: 'Client' }, ...HOLDING_COLUMNS],
                rows: holdingRows(scoped, fundPrices, request.fundIds)
            };

        case REQUEST_TYPES.ACCOUNT_STATEMENT:
        case REQUEST_TYPES.OTHER:
        default: {
            // A statement is position AND activity: holdings first, then the period's transactions.
            const holdings = holdingRows(scoped, fundPrices, request.fundIds).map((row) => ({
                ...row,
                section: 'Holding',
                transactionId: '',
                transactionDate: '',
                kind: '',
                amount: row.value,
                status: ''
            }));
            const activity = transactionRows(transactions, accountIds, request).map((row) => ({
                ...row,
                section: 'Transaction',
                strategyName: row.strategyName,
                activationCode: '',
                value: row.amount
            }));

            return {
                columns: [
                    { key: 'section', title: 'Section' },
                    { key: 'transactionDate', title: 'Date' },
                    { key: 'transactionId', title: 'Reference', mono: true },
                    { key: 'kind', title: 'Transaction' },
                    { key: 'strategyName', title: 'Strategy' },
                    { key: 'fundId', title: 'Fund' },
                    { key: 'units', title: 'Units', align: 'right' },
                    { key: 'unitPrice', title: 'Unit price', align: 'right' },
                    { key: 'value', title: 'Value (LKR)', align: 'right' },
                    { key: 'status', title: 'Status' }
                ],
                rows: [...holdings, ...activity]
            };
        }
    }
};

/** Human label for a generated document row. */
export const describeRequest = (request, accounts) => {
    const type = REQUEST_TYPE_OPTIONS.find((t) => t.value === request.reportType)?.label;
    const account = accounts.find((a) => a.accountId === request.accountId);
    const scope = account ? account.accountName : 'All clients';
    return { type, scope, segment: SEGMENT_LABELS[account?.segment] || '—' };
};

/** `TaxReport_UTIF_2025-04-01_2026-03-31.csv` — recognisable at the bottom of a browser. */
export const documentFileName = (request) =>
    [
        request.reportType,
        request.fundId || null,
        request.startDate || null,
        request.endDate || null
    ]
        .filter(Boolean)
        .join('_')
        .replace(/[^A-Za-z0-9_\-.]/g, '') + '.csv';
